import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle } from 'styled-components';
import { useDispatch } from 'react-redux';
import SEO from '#/shared/components/SEO';
import useViewportDimensions from '#/shared/hooks/useViewportDimensions';
import { focusCssRule } from '#/shared/css/focus';
import { viewportWidthChanged } from '#/user/slice';
import ThemeContainer from '#/shared/providers/ThemeContainer';
import { DEFAULT_FONT_FAMILY, DESKTOP_MIN_BREAK } from '#/shared/constants';

const PageContainer = styled.div`
  margin: 0 auto;
  padding-top: 0;
  font-family: ${DEFAULT_FONT_FAMILY};

  div,
  button {
    font-family: inherit;
  }

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    padding: 0;
  }
`;

const GlobalStyle = createGlobalStyle`
  * {
    padding: 0;
    margin: 0;
    line-height: 1.25;
  }

  ${focusCssRule({ type: 'default' })}

  /* The only way to remove the extra button highlight on tap devices
  https://newbedev.com/anyway-to-prevent-the-blue-highlighting-of-elements-in-chrome-when-clicking-quickly
  */
  button,
  a,
  input {
    -webkit-tap-highlight-color: transparent;
  }
  .leaflet-popup-content {
    margin: 18px 13px 13px 13px;
  }

  .leaflet-container a.leaflet-popup-close-button{
    padding: 6px 6px 0 0;
  }

  /* Override all animations if user has set animations off  */
  @media (prefers-reduced-motion: reduce) {
    *,
    ::before,
    ::after {
      animation-delay: -1ms !important;
      animation-duration: 1ms !important;
      animation-iteration-count: 1 !important;
      background-attachment: initial !important;
      scroll-behavior: auto !important;
      transition-duration: 0s !important;
      transition-delay: 0s !important;
    }
  }

  .basic-flex {
    display: flex;
  }

  .skeleton-flex-0 {
    display: flex;
    flex: 0 0 auto;
  }
  .skeleton-flex-1 {
    display: flex;
    flex: 1 0 auto;
  }
  .skeleton-flex-half {
    display: flex;
    flex: 0.5 0 auto;
  }
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

function PageLayout({ children, pageIsInert }) {
  const dispatch = useDispatch();
  const { width: viewportWidth } = useViewportDimensions();

  // track whether the app is in mobile or desktop mode based on the
  // viewport width and store that value in the Redux state
  useEffect(() => {
    dispatch(viewportWidthChanged(viewportWidth));
  }, [dispatch, viewportWidth]);

  return (
    <>
      <SEO metadataId="home" useTitleSuffix={false} />
      {/*
       * inert is a boolean attribute but isn't treated as such by React until React 19:
       * https://github.com/facebook/react/issues/17157#issuecomment-2003750544
       */}
      <PageContainer inert={pageIsInert ? '' : undefined}>
        <ThemeContainer>
          <Main>{children}</Main>
        </ThemeContainer>
      </PageContainer>
      <style>
        @import
        &quot;https://fonts.googleapis.com/css?family=Source+Sans+Pro:300,400,600,700&quot;
      </style>
      <GlobalStyle />
    </>
  );
}

PageLayout.propTypes = {
  children: PropTypes.node.isRequired,
  pageIsInert: PropTypes.bool,
};

PageLayout.defaultProps = {
  pageIsInert: false,
};

export default PageLayout;
